import { useCallback, useEffect, useState } from 'react';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { Sheet, fmtTime, fmtDateLong } from '../design/atoms';
import WeeklySlots, { WEEKDAYS, dayMonth } from './WeeklySlots';

/* "Every week" — the member's standing slots in My bookings. Each slot shows
   its coach, its weekly time and a strip of the coming weeks; tapping a week
   skips it (the session goes back on the plan) or brings a skipped week back.
   Moving the slot opens the same weekly grid as checkout, limited to times
   free on every remaining week. Renders nothing when there are no slots. */

type OccState = 'booked' | 'held' | 'skipped' | 'paused' | 'cancelled' | 'attended' | 'missed';
interface Occurrence { date: string; state: OccState; bookingId: string | null; skipReason: string | null; issue: { id: string; kind: string; detail: string | null } | null }
interface Series {
  id: string;
  offeringName: string;
  trainer: { id: string; name: string; photoUrl: string | null };
  className: string;
  branch: { id: string; name: string };
  weekday: number;
  startTime: string;
  durationMin: number;
  startsOn: string;
  endsOn: string | null;
  upcoming: Occurrence[];
}
interface StandingData { today: string; memberCanMove: boolean; series: Series[] }

const STATE_LABEL: Record<OccState, string> = {
  booked: 'Booked', held: 'Held', skipped: 'Skipped', paused: 'Paused', cancelled: 'Cancelled', attended: 'Done', missed: 'Missed',
};

export default function StandingSlots({ token, gymId, onChanged }: { token: string; gymId: string; onChanged?: () => void }) {
  const [data, setData] = useState<StandingData | null>(null);
  const [week, setWeek] = useState<{ s: Series; o: Occurrence } | null>(null);
  const [moving, setMoving] = useState<Series | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(() => {
    mfetch(token, '/member/standing', { params: { gymId } })
      .then(r => setData(r.data))
      .catch(() => setData({ today: '', memberCanMove: false, series: [] })); // failure-silent: the section just doesn't show
  }, [token, gymId]);
  useEffect(load, [load]);

  const act = async (path: string, body: any, done: string) => {
    setBusy(true);
    setProblem(null);
    try {
      await mfetch(token, path, { method: 'POST', body });
      setWeek(null);
      setMoving(null);
      setToast(done);
      setTimeout(() => setToast(null), 2600);
      load();
      onChanged?.();
    } catch (e) {
      setProblem(e instanceof MemberApiError ? e.message : 'That didn’t go through — try again');
    } finally {
      setBusy(false);
    }
  };

  if (!data || data.series.length === 0) return null;

  return (
    <>
      <div className="lbl">Every week</div>
      {data.series.map(s => (
        <div key={s.id} className="card" style={{ padding: 16 }}>
          <div className="col" style={{ gap: 12 }}>
            <div className="row" style={{ gap: 12 }}>
              {s.trainer.photoUrl
                ? <img src={s.trainer.photoUrl} alt="" style={{ width: 44, height: 44, borderRadius: 22, objectFit: 'cover', flex: '0 0 auto' }} />
                : <div className="avatar" style={{ width: 44, height: 44, flex: '0 0 auto' }}><Icon n="user" s={20} /></div>}
              <div className="col" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                <div className="mid trunc">Every {WEEKDAYS[s.weekday]} · {fmtTime(s.startTime)}</div>
                <div className="tiny trunc">{s.trainer.name} · {s.durationMin} min · {s.className}</div>
                <div className="tiny trunc">{s.offeringName}{s.endsOn ? ` · until ${fmtDateLong(s.endsOn)}` : ''}</div>
              </div>
            </div>

            {s.upcoming.length > 0 && (
              <div className="occ-strip">
                {s.upcoming.map(o => {
                  const off = ['skipped', 'paused', 'cancelled'].includes(o.state);
                  const tappable = ['booked', 'held'].includes(o.state) || (o.state === 'skipped' && o.skipReason === 'member');
                  return (
                    <button
                      key={o.date}
                      className={`occ${o.state === 'booked' ? ' booked' : ''}${off ? ' off' : ''}${o.issue ? ' flag' : ''}`}
                      disabled={!tappable}
                      onClick={() => { setProblem(null); setWeek({ s, o }); }}
                      aria-label={`${dayMonth(o.date)}: ${STATE_LABEL[o.state]}`}
                    >
                      <span>{dayMonth(o.date).split(' ')[1]}</span>
                      <b className="num">{Number(o.date.slice(8, 10))}</b>
                      <span style={{ fontSize: 9 }}>{STATE_LABEL[o.state]}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {data.memberCanMove && (
              <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => { setProblem(null); setMoving(s); }}>
                <Icon n="clock" s={15} /> Change weekly time
              </button>
            )}
          </div>
        </div>
      ))}

      {toast && <div className="card ink" style={{ padding: 12 }}><div className="sub" style={{ color: 'var(--on-ink)' }}>{toast}</div></div>}

      {week && (
        <Sheet onClose={() => setWeek(null)}>
          <div className="col" style={{ gap: 2 }}>
            <div className="dp dp-m">{fmtDateLong(week.o.date)}</div>
            <div className="sub trunc">{fmtTime(week.s.startTime)} with {week.s.trainer.name}</div>
          </div>
          {week.o.issue && (
            <div className="card flat" style={{ padding: 12 }}>
              <div className="sub wrap">{week.o.issue.detail ?? 'The gym is sorting out this week'} — the front desk will be in touch.</div>
            </div>
          )}
          {problem && <div className="card flat" style={{ padding: 12 }}><div className="sub wrap">{problem}</div></div>}
          {week.o.state === 'skipped' ? (
            <>
              <div className="sub wrap">You skipped this week. Bring it back if the time is still free.</div>
              <button className="btn gold block" disabled={busy} onClick={() => act(`/member/standing/${week.s.id}/restore`, { date: week.o.date }, 'Week restored')}>
                {busy ? 'Restoring…' : 'Bring this week back'}
              </button>
            </>
          ) : (
            <>
              <div className="sub wrap">Skip just this week — your weekly time stays, and the session goes back on your plan.</div>
              <button className="btn block" disabled={busy} onClick={() => act(`/member/standing/${week.s.id}/skip`, { date: week.o.date }, 'Week skipped')}>
                {busy ? 'Skipping…' : 'Skip this week'}
              </button>
            </>
          )}
        </Sheet>
      )}

      {moving && (
        <WeeklySlots
          token={token}
          url={`/member/standing/${moving.id}/grid`}
          title="Change weekly time"
          count={{ min: 1, max: 1 }}
          problem={problem}
          busy={busy}
          onClose={() => setMoving(null)}
          onChoose={c => act(`/member/standing/${moving.id}/move`, c.times[0], `Now every ${WEEKDAYS[c.times[0].weekday]} ${fmtTime(c.times[0].startTime)}`)}
        />
      )}
    </>
  );
}
